import { gql } from "@apollo/client";
import { PriceableItemOrigin } from "elephant-web/gql";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useGetCustomItemsLazyQuery } from "./__generated__/use-search-custom-item-lazy-query.gql";
import { sortFilterAndPaginate } from "./helpers";
import type {
  SearchNewItemData,
  SearchNewItemDataRow,
  SearchNewItemHook,
  SearchNewItemHookResult,
  SearchNewItemParams,
} from "./types";

gql`
  query getCustomItems($priceListId: ID!) {
    priceListV2 {
      customItems(input: { priceListId: $priceListId }) {
        items {
          id
          name
        }
      }
    }
  }
`;

const mapGqlResponseToResult = (
  response: ReturnType<typeof useGetCustomItemsLazyQuery>[1],
  params: SearchNewItemParams,
): SearchNewItemData => {
  const customItems: SearchNewItemDataRow[] =
    response.data?.priceListV2.customItems.items.map((item) => ({
      id: item.id,
      name: item.name,
      origin: PriceableItemOrigin.Custom,
    })) ?? [];

  const rows = sortFilterAndPaginate(customItems, params);
  const data = {
    rows: rows ?? [],
    totalRows: rows?.length,
  };

  return {
    ...response,
    data,
  };
};

const useSearchCustomItemLazyQuery: SearchNewItemHook = () => {
  const { id: priceListId } = useParams();
  const [data, setData] = useState<SearchNewItemData>({
    loading: false,
    data: { rows: [], totalRows: 0 },
  });

  const [query] = useGetCustomItemsLazyQuery({
    variables: { priceListId: priceListId! },
    fetchPolicy: "network-only",
  });

  const wrappedQuery: SearchNewItemHookResult[0] = useMemo(
    () => (args) =>
      query().then((res) => {
        const newData = mapGqlResponseToResult(res, args);
        setData(newData);
        return newData;
      }),
    [query],
  );

  return [wrappedQuery, data];
};

export default useSearchCustomItemLazyQuery;
